import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Dialog,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Tooltip,
  Drawer,
  Avatar,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  collection,
  onSnapshot,
  query,
  getFirestore,
  doc,
  writeBatch,
  getDocs,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { usePermissions } from '../AuthContext';

interface CrmUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  roleId?: string;
  dateOfBirth?: string | null;
  dob?: string | null;
  pageAccess?: string[];
  actionAccess?: string[];
  projectIds?: string[];
  reportsToUserId?: string | null;
}

interface LeadAssignmentRow {
  id: string;
  name?: string;
  assignedTo?: string | null;
  assignedToName?: string | null;
}

export default function UserList({ onAddUser }: { onAddUser: () => void }) {
  const [users, setUsers] = useState<CrmUser[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [leads, setLeads] = useState<LeadAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [isScanned, setIsScanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canAction } = usePermissions();
  const canManage = canAction('settings', 'addUser');

  const roles = [
    { id: 'super-admin', label: 'Admin' },
    { id: 'sales-manager', label: 'Sales Manager' },
    { id: 'sales-team', label: 'Sales Team' },
    { id: 'trainee', label: 'Trainee' },
    { id: 'front-desk', label: 'Front Desk' },
  ];
  const allPages = [
    { key: 'today', label: 'Today' },
    { key: 'frontDesk', label: 'Front Desk' },
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'leads', label: 'Leads' },
    { key: 'followUps', label: 'Follow-ups' },
    { key: 'brokers', label: 'Brokers' },
    { key: 'jobApplications', label: 'Job Applications' },
    { key: 'messages', label: 'Messages' },
    { key: 'metaLeads', label: 'Meta Leads' },
    { key: 'reports', label: 'Reports' },
    // Settings should remain super-admin only; don't expose it as assignable page access.
  ];
  const pageActions: Record<string, { key: string; label: string }[]> = {
    today: [],
    frontDesk: [],
    dashboard: [],
    leads: [
      { key: 'addLead', label: 'Add Lead' },
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'changeAssign', label: 'Assign Change' },
      { key: 'updateFollowUp', label: 'Update Follow-up' },
      { key: 'editLead', label: 'Edit Lead' },
    ],
    followUps: [
      { key: 'addLead', label: 'Add Lead' },
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'changeAssign', label: 'Assign Change' },
      { key: 'updateFollowUp', label: 'Update Follow-up' },
    ],
    brokers: [
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'editBroker', label: 'Edit Broker' },
      { key: 'deleteBroker', label: 'Delete Broker' },
    ],
    jobApplications: [],
    messages: [],
    reports: [],
  };

  // edit state
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CrmUser | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [roleId, setRoleId] = useState('sales-team');
  const [pageAccess, setPageAccess] = useState<string[]>([]);
  const [actionAccess, setActionAccess] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [reportsToUserId, setReportsToUserId] = useState('');
  const [directReportIds, setDirectReportIds] = useState<string[]>([]);
  const [editorTab, setEditorTab] = useState<'profile' | 'access'>('profile');
  const [viewTab, setViewTab] = useState<'people' | 'audit'>('people');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [orphanTargets, setOrphanTargets] = useState<Record<string, string>>({});
  const [transferingEmail, setTransferingEmail] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [deleteDialogError, setDeleteDialogError] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    user: CrmUser;
    leadCount: number;
    leadNames: string[];
  } | null>(null);

  useEffect(() => {
    const db = getFirestore();
    const usersQuery = query(collection(db, 'users'));

    const unsubscribe = onSnapshot(
      usersQuery,
      (querySnapshot) => {
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as CrmUser[];
        setUsers(usersData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching users:', err);
        setError('Failed to fetch users. Please try again later.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const db = getFirestore();
    const unsub = onSnapshot(
      query(collection(db, 'projects')),
      (snap) => setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setProjects([])
    );
    return () => unsub();
  }, []);

  const startOrphanScan = async () => {
    if (!canManage) return;
    setLeadsLoading(true);
    setTransferError(null);
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(collection(db, 'leads'), where('assignedTo', '!=', null))
      );
      setLeads(
        snap.docs.map((leadDoc) => ({
          id: leadDoc.id,
          name: String(leadDoc.data()?.name || '').trim(),
          assignedTo: leadDoc.data()?.assignedTo ?? null,
          assignedToName: leadDoc.data()?.assignedToName ?? null,
        }))
      );
      setIsScanned(true);
    } catch (err) {
      console.error('Error fetching lead assignments:', err);
      setTransferError('Failed to load lead assignments.');
    } finally {
      setLeadsLoading(false);
    }
  };

  const asStringArray = (v: any): string[] => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim());
    return [];
  };

  const activeUserEmails = useMemo(
    () => new Set(users.map((u) => String(u.email || '').trim().toLowerCase()).filter(Boolean)),
    [users]
  );

  const orphanedAssignments = useMemo(() => {
    const grouped = new Map<string, { email: string; count: number; names: string[] }>();
    for (const lead of leads) {
      const email = String(lead.assignedTo || '').trim();
      const normalized = email.toLowerCase();
      if (!normalized || activeUserEmails.has(normalized)) continue;
      const bucket = grouped.get(normalized) || { email, count: 0, names: [] };
      bucket.count += 1;
      if (lead.name && bucket.names.length < 3) bucket.names.push(lead.name);
      grouped.set(normalized, bucket);
    }
    return Array.from(grouped.values()).sort((a, b) => a.email.localeCompare(b.email));
  }, [activeUserEmails, leads]);

  const sortedUsers = useMemo(() => users.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))), [users]);
  const filteredUsers = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    return sortedUsers.filter((user) => {
      const matchesSearch = !queryText || [user.name, user.email, user.phone].some((value) => String(value || '').toLowerCase().includes(queryText));
      const userRole = String(user.roleId || user.role);
      const matchesRole = roleFilter === 'all' || userRole === roleFilter || (roleFilter === 'super-admin' && userRole === 'admin');
      const matchesProject = projectFilter === 'all' || asStringArray(user.projectIds).includes(projectFilter);
      return matchesSearch && matchesRole && matchesProject;
    });
  }, [sortedUsers, search, roleFilter, projectFilter]);
  const salesManagers = useMemo(() => sortedUsers.filter((user) => String(user.roleId || user.role) === 'sales-manager'), [sortedUsers]);
  const salesTeam = useMemo(() => sortedUsers.filter((user) => String(user.roleId || user.role) === 'sales-team'), [sortedUsers]);

  const setOrphanTarget = (email: string, value: string) => {
    setOrphanTargets((prev) => ({ ...prev, [email]: value }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const toDomainAction = (pageKey: string, actionKey: string) => {
    // Follow-ups actions map to the same underlying "leads" domain permissions.
    const domain =
      pageKey === 'followUps' ? 'leads' :
      pageKey === 'leads' ? 'leads' :
      pageKey === 'brokers' ? 'brokers' :
      pageKey === 'settings' ? 'settings' :
      pageKey;
    return `${domain}.${actionKey}`;
  };

  const allowedActionValuesForPages = (pages: string[]) => {
    const allowed = new Set<string>();
    for (const pageKey of pages) {
      const acts = pageActions[pageKey] || [];
      for (const a of acts) allowed.add(toDomainAction(pageKey, a.key));
    }
    return allowed;
  };

  const isDomainActionSelected = (pageKey: string, actionKey: string, selected: string[]) => {
    const v = toDomainAction(pageKey, actionKey);
    return selected.includes(v);
  };

  const handleEditClick = (user: CrmUser) => {
    setSelectedUser(user);
    setName(user.name);
    setPhone(user.phone);
    setDateOfBirth(String(user.dateOfBirth || user.dob || ''));
    setRoleId(user.roleId || user.role || 'sales-team');
    setPageAccess(asStringArray(user.pageAccess));
    // Normalize legacy stored values:
    // - old format: "followUps.updateFollowUp" etc
    // - new format: "leads.updateFollowUp" etc (domain-based)
    setActionAccess(
      asStringArray(user.actionAccess).map((v) => {
        if (!v.includes('.')) return v;
        const [left, right] = v.split('.');
        return toDomainAction(left, right);
      })
    );
    setProjectIds(asStringArray(user.projectIds));
    setReportsToUserId(String(user.reportsToUserId || ''));
    setDirectReportIds(salesTeam.filter((member) => member.reportsToUserId === user.id).map((member) => member.id));
    setEditorTab('profile');
    setSaveError(null);
    setOpen(true);
  };

  const handleDelete = async (user: CrmUser) => {
    if (!canManage) return;
    try {
      const db = getFirestore();
      const assignedSnap = await getDocs(
        query(collection(db, 'leads'), where('assignedTo', '==', user.email))
      );
      if (!assignedSnap.empty) {
        setPendingDelete({
          user,
          leadCount: assignedSnap.size,
          leadNames: assignedSnap.docs
            .map((leadDoc) => String(leadDoc.data()?.name || '').trim())
            .filter(Boolean)
            .slice(0, 5),
        });
        setDeleteTarget('');
        setDeleteDialogError(null);
        setDeleteDialogOpen(true);
        return;
      }
      const confirmed = window.confirm(`Delete user ${user.name}?`);
      if (!confirmed) return;
      const batch = writeBatch(db);
      batch.delete(doc(db, 'users', user.id));
      if (String(user.roleId || user.role) === 'sales-manager') {
        salesTeam.filter((member) => member.reportsToUserId === user.id).forEach((member) => {
          batch.update(doc(db, 'users', member.id), { reportsToUserId: null });
        });
      }
      if (user.phone) batch.delete(doc(db, 'userPhoneNumbers', user.phone));
      await batch.commit();
    } catch (err) {
      console.error('Delete user failed', err);
      setError('Failed to delete user.');
    }
  };

  const handleDeleteWithTransfer = async () => {
    if (!pendingDelete || !canManage) return;
    const targetValue = String(deleteTarget || '').trim();
    if (!targetValue) {
      setDeleteDialogError('Select a new assignee or choose Unassigned.');
      return;
    }

    setDeletingUser(true);
    setDeleteDialogError(null);
    try {
      const db = getFirestore();
      const assignedSnap = await getDocs(
        query(collection(db, 'leads'), where('assignedTo', '==', pendingDelete.user.email))
      );
      const targetUser = users.find((u) => String(u.email || '').trim() === targetValue);
      const chunkSize = 400;

      for (let i = 0; i < assignedSnap.docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        assignedSnap.docs.slice(i, i + chunkSize).forEach((leadDoc) => {
          batch.update(doc(db, 'leads', leadDoc.id), {
            assignedTo: targetValue === '__unassigned__' ? null : targetValue,
            assignedAt: targetValue === '__unassigned__' ? null : serverTimestamp(),
            assignedToName:
              targetValue === '__unassigned__' ? null : String(targetUser?.name || '').trim() || null,
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      const deleteBatch = writeBatch(db);
      deleteBatch.delete(doc(db, 'users', pendingDelete.user.id));
      if (String(pendingDelete.user.roleId || pendingDelete.user.role) === 'sales-manager') {
        salesTeam.filter((member) => member.reportsToUserId === pendingDelete.user.id).forEach((member) => {
          deleteBatch.update(doc(db, 'users', member.id), { reportsToUserId: null });
        });
      }
      if (pendingDelete.user.phone) {
        deleteBatch.delete(doc(db, 'userPhoneNumbers', pendingDelete.user.phone));
      }
      await deleteBatch.commit();

      setTransferSuccess(
        `${pendingDelete.user.name}'s ${pendingDelete.leadCount} lead` +
          `${pendingDelete.leadCount === 1 ? '' : 's'} moved to ` +
          `${targetValue === '__unassigned__' ? 'Unassigned' : targetUser?.name || targetValue} and user deleted.`
      );
      setDeleteDialogOpen(false);
      setPendingDelete(null);
      setDeleteTarget('');
    } catch (err) {
      console.error('Delete with transfer failed', err);
      setDeleteDialogError('Failed to transfer leads and delete user.');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleTransferOrphanedLeads = async (fromEmail: string) => {
    if (!canManage) return;
    const targetValue = String(orphanTargets[fromEmail] || '').trim();
    if (!targetValue) {
      setTransferError(`Select a transfer target for ${fromEmail}.`);
      return;
    }
    setTransferingEmail(fromEmail);
    setTransferError(null);
    setTransferSuccess(null);
    try {
      const db = getFirestore();
      const orphanSnap = await getDocs(
        query(collection(db, 'leads'), where('assignedTo', '==', fromEmail))
      );
      if (orphanSnap.empty) {
        setTransferSuccess(`No leads remain assigned to ${fromEmail}.`);
        return;
      }

      const targetUser = users.find((u) => String(u.email || '').trim() === targetValue);
      const chunkSize = 400;
      for (let i = 0; i < orphanSnap.docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        orphanSnap.docs.slice(i, i + chunkSize).forEach((leadDoc) => {
          const leadRef = doc(db, 'leads', leadDoc.id);
          batch.update(leadRef, {
            assignedTo: targetValue === '__unassigned__' ? null : targetValue,
            assignedAt: targetValue === '__unassigned__' ? null : serverTimestamp(),
            assignedToName:
              targetValue === '__unassigned__' ? null : String(targetUser?.name || '').trim() || null,
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      setTransferSuccess(
        `${orphanSnap.size} lead${orphanSnap.size === 1 ? '' : 's'} moved from ${fromEmail} ` +
          `to ${targetValue === '__unassigned__' ? 'Unassigned' : targetUser?.name || targetValue}.`
      );
    } catch (err) {
      console.error('Transfer orphaned leads failed', err);
      setTransferError(`Failed to transfer leads from ${fromEmail}.`);
    } finally {
      setTransferingEmail(null);
    }
  };

  const handleSave = async () => {
    if (!selectedUser || !canManage) return;
    setSaving(true);
    setSaveError(null);
    try {
      const nextName = name.trim();
      if (!nextName) throw new Error('Enter a full name.');
      if (roleId === 'sales-team' && reportsToUserId && (reportsToUserId === selectedUser.id || !salesManagers.some((manager) => manager.id === reportsToUserId))) {
        throw new Error('Choose an active Sales Manager.');
      }
      const db = getFirestore();
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', selectedUser.id);

      if (selectedUser.phone && selectedUser.phone !== phone) {
        batch.delete(doc(db, 'userPhoneNumbers', selectedUser.phone));
      }
      if (phone) {
        batch.set(doc(db, 'userPhoneNumbers', phone), { userId: selectedUser.id });
      }

      batch.update(userRef, {
        name: nextName,
        phone,
        dateOfBirth: dateOfBirth || null,
        dob: dateOfBirth || null,
        roleId,
        pageAccess,
        actionAccess,
        projectIds,
        reportsToUserId: roleId === 'sales-team' ? reportsToUserId || null : null,
      });

      const wasManager = String(selectedUser.roleId || selectedUser.role) === 'sales-manager';
      if (roleId === 'sales-manager' || wasManager) {
        salesTeam.forEach((member) => {
          const assignedHere = member.reportsToUserId === selectedUser.id;
          const shouldAssignHere = roleId === 'sales-manager' && directReportIds.includes(member.id);
          if (assignedHere !== shouldAssignHere) {
            batch.update(doc(db, 'users', member.id), { reportsToUserId: shouldAssignHere ? selectedUser.id : null });
          }
        });
      }

      await batch.commit();
      setOpen(false);
    } catch (err: any) {
      console.error('Update user failed', err);
      setSaveError(err.message || 'Failed to update user.');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (user: CrmUser) => String(user.roleId || user.role) === 'admin' ? 'Admin' : roles.find((role) => role.id === String(user.roleId || user.role))?.label || String(user.roleId || user.role || '—');
  const projectSummary = (user: CrmUser) => {
    const ids = asStringArray(user.projectIds);
    if (!ids.length) return 'No projects';
    return ids.map((id) => projects.find((project) => project.id === id)?.name || id).join(', ');
  };
  const accessSummary = (user: CrmUser) => {
    const pages = asStringArray(user.pageAccess).length;
    const actions = new Set(asStringArray(user.actionAccess).map((value) => {
      const [domain, action] = value.split('.');
      return action ? toDomainAction(domain, action) : value;
    })).size;
    return `${pages} page${pages === 1 ? '' : 's'} · ${actions} action${actions === 1 ? '' : 's'}`;
  };
  const togglePage = (key: string, checked: boolean) => {
    const nextPages = checked ? Array.from(new Set([...pageAccess, key])) : pageAccess.filter((page) => page !== key);
    setPageAccess(nextPages);
    const allowed = allowedActionValuesForPages(nextPages);
    setActionAccess((previous) => previous.filter((action) => allowed.has(action)));
  };
  const toggleAction = (value: string, checked: boolean) => {
    setActionAccess((previous) => checked ? Array.from(new Set([...previous, value])) : previous.filter((action) => action !== value));
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 28, sm: 34 }, lineHeight: 1.15, color: '#24221f' }}>Team access</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>Manage people, projects, and what each person can do.</Typography>
        </Box>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={onAddUser} sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'auto' } }}>Add user</Button>}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#e8e4dd' }}>
        <Stack direction="row" spacing={3} sx={{ px: 2.5, borderBottom: '1px solid #e8e4dd' }}>
          <Button onClick={() => setViewTab('people')} sx={{ minHeight: 50, px: 0, borderRadius: 0, color: viewTab === 'people' ? 'primary.main' : 'text.secondary', borderBottom: viewTab === 'people' ? '3px solid' : '3px solid transparent', fontWeight: 700 }}>People · {users.length}</Button>
          {canManage && <Button onClick={() => setViewTab('audit')} sx={{ minHeight: 50, px: 0, borderRadius: 0, color: viewTab === 'audit' ? 'primary.main' : 'text.secondary', borderBottom: viewTab === 'audit' ? '3px solid' : '3px solid transparent', fontWeight: 700 }}>Assignment audit</Button>}
        </Stack>

        {viewTab === 'people' ? (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.25} sx={{ p: 2 }}>
              <TextField size="small" placeholder="Search name, email or phone" aria-label="Search users" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ width: { xs: '100%', md: 340 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 145 } }}>
                  <InputLabel id="user-role-filter-label">Role</InputLabel>
                  <Select labelId="user-role-filter-label" label="Role" value={roleFilter} onChange={(event) => setRoleFilter(String(event.target.value))}>
                    <MenuItem value="all">All roles</MenuItem>
                    {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 175 } }}>
                  <InputLabel id="user-project-filter-label">Project</InputLabel>
                  <Select labelId="user-project-filter-label" label="Project" value={projectFilter} onChange={(event) => setProjectFilter(String(event.target.value))}>
                    <MenuItem value="all">All projects</MenuItem>
                    {projects.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))).map((project) => <MenuItem key={project.id} value={project.id}>{project.name || project.id}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table aria-label="CRM users">
                <TableHead sx={{ bgcolor: '#faf9f7' }}><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Person</TableCell><TableCell sx={{ fontWeight: 700 }}>Role</TableCell><TableCell sx={{ fontWeight: 700 }}>Projects</TableCell><TableCell sx={{ fontWeight: 700 }}>Access</TableCell>{canManage && <TableCell align="right" sx={{ fontWeight: 700 }}>Manage</TableCell>}
                </TableRow></TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell><Stack direction="row" alignItems="center" spacing={1.25}><Avatar sx={{ width: 38, height: 38, bgcolor: '#efe6d8', color: '#805421', fontSize: 14, fontWeight: 700 }}>{String(user.name || '?').charAt(0).toUpperCase()}</Avatar><Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 700 }}>{user.name || 'Unnamed user'}</Typography><Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{user.email}</Typography></Box></Stack></TableCell>
                      <TableCell><Chip label={roleLabel(user)} size="small" sx={{ fontWeight: 700, bgcolor: '#f4f1eb' }} />{String(user.roleId || user.role) === 'sales-manager' && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>{salesTeam.filter((member) => member.reportsToUserId === user.id).length} direct reports</Typography>}{String(user.roleId || user.role) === 'sales-team' && user.reportsToUserId && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>Reports to {users.find((manager) => manager.id === user.reportsToUserId)?.name || 'Unassigned'}</Typography>}</TableCell>
                      <TableCell sx={{ maxWidth: 210 }}>{projectSummary(user)}</TableCell><TableCell><Typography variant="body2" color="text.secondary">{accessSummary(user)}</Typography></TableCell>
                      {canManage && <TableCell align="right"><Stack direction="row" justifyContent="flex-end" spacing={0.5}><Button variant="outlined" size="small" onClick={() => handleEditClick(user)} sx={{ whiteSpace: 'nowrap' }}>Manage access</Button><Tooltip title={`Delete ${user.name}`}><IconButton aria-label={`Delete ${user.name}`} size="small" onClick={() => handleDelete(user)}><DeleteIcon fontSize="small" /></IconButton></Tooltip></Stack></TableCell>}
                    </TableRow>
                  ))}
                  {!filteredUsers.length && <TableRow><TableCell colSpan={canManage ? 5 : 4} align="center" sx={{ py: 5, color: 'text.secondary' }}>No people match these filters.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack sx={{ display: { xs: 'flex', md: 'none' }, px: 1.5, pb: 1.5 }} spacing={1}>
              {filteredUsers.map((user) => <Paper key={user.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}><Stack direction="row" spacing={1.25} alignItems="center"><Avatar sx={{ width: 38, height: 38, bgcolor: '#efe6d8', color: '#805421', fontSize: 14 }}>{String(user.name || '?').charAt(0).toUpperCase()}</Avatar><Box sx={{ minWidth: 0, flex: 1 }}><Typography fontWeight={700}>{user.name || 'Unnamed user'}</Typography><Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{user.email}</Typography></Box></Stack><Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25 }}><Typography variant="body2">{roleLabel(user)}</Typography><Typography variant="body2" color="text.secondary">{accessSummary(user)}</Typography></Stack><Typography variant="caption" color="text.secondary">{projectSummary(user)}</Typography>{canManage && <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}><Button variant="outlined" size="small" onClick={() => handleEditClick(user)}>Manage access</Button><IconButton aria-label={`Delete ${user.name}`} size="small" onClick={() => handleDelete(user)}><DeleteIcon fontSize="small" /></IconButton></Stack>}</Paper>)}
              {!filteredUsers.length && <Typography color="text.secondary" sx={{ p: 2 }}>No people match these filters.</Typography>}
            </Stack>
          </>
        ) : (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: 23 }}>Assignment audit</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 680 }}>Check for leads assigned to deleted users, then transfer them to an active person or mark them unassigned.</Typography>
            {transferError && <Alert severity="error" sx={{ mb: 2 }}>{transferError}</Alert>}
            {transferSuccess && <Alert severity="success" sx={{ mb: 2 }}>{transferSuccess}</Alert>}
            {leadsLoading ? <Stack direction="row" alignItems="center" spacing={1}><CircularProgress size={18} /><Typography>Checking assignments…</Typography></Stack> : !isScanned ? <Button variant="outlined" onClick={startOrphanScan}>Scan assignments</Button> : orphanedAssignments.length === 0 ? <Alert severity="success" variant="outlined">No leads are assigned to deleted users.</Alert> : <Stack spacing={1.5}>{orphanedAssignments.map((row) => <Paper key={row.email} variant="outlined" sx={{ p: 1.5 }}><Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1.5}><Box><Typography fontWeight={700}>{row.email}</Typography><Typography variant="body2" color="text.secondary">{row.count} lead{row.count === 1 ? '' : 's'}{row.names.length ? ` · e.g. ${row.names.join(', ')}` : ''}</Typography></Box><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ minWidth: { md: 420 } }}><FormControl fullWidth size="small"><InputLabel id={`orphan-target-${row.email}`}>Transfer to</InputLabel><Select labelId={`orphan-target-${row.email}`} label="Transfer to" value={orphanTargets[row.email] || ''} onChange={(event) => setOrphanTarget(row.email, String(event.target.value || ''))}><MenuItem value="__unassigned__">Unassigned</MenuItem>{sortedUsers.map((user) => <MenuItem key={user.id} value={user.email}>{user.name} ({user.email})</MenuItem>)}</Select></FormControl><Button variant="contained" disabled={!orphanTargets[row.email] || transferingEmail === row.email} onClick={() => handleTransferOrphanedLeads(row.email)}>{transferingEmail === row.email ? 'Transferring…' : 'Transfer'}</Button></Stack></Stack></Paper>)}</Stack>}
          </Box>
        )}
      </Paper>

      <Drawer anchor="right" open={open} onClose={() => !saving && setOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, maxWidth: '100vw', top: { xs: 56, sm: 64 }, height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' } } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, borderBottom: '1px solid #e8e4dd' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start"><Box><Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.1em' }}>User management</Typography><Typography sx={{ fontFamily: 'Georgia, serif', fontSize: 30, lineHeight: 1.2 }}>Edit {selectedUser?.name || 'user'}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{selectedUser?.email}</Typography></Box><IconButton aria-label="Close edit user" onClick={() => setOpen(false)}><CloseIcon /></IconButton></Stack>
            <Stack direction="row" spacing={2.5} sx={{ mt: 2 }}><Button onClick={() => setEditorTab('profile')} sx={{ borderRadius: 0, minHeight: 44, px: 0, borderBottom: editorTab === 'profile' ? '3px solid' : '3px solid transparent', fontWeight: 700, color: editorTab === 'profile' ? 'primary.main' : 'text.secondary' }}>Profile</Button><Button onClick={() => setEditorTab('access')} sx={{ borderRadius: 0, minHeight: 44, px: 0, borderBottom: editorTab === 'access' ? '3px solid' : '3px solid transparent', fontWeight: 700, color: editorTab === 'access' ? 'primary.main' : 'text.secondary' }}>Access & permissions</Button></Stack>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, overflowY: 'auto', flex: 1, bgcolor: '#fcfbf9' }}>
            {editorTab === 'profile' ? <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Personal details</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Details used to identify and contact this team member.</Typography><Stack spacing={2}><TextField label="Full name" value={name} onChange={(event) => setName(event.target.value)} required fullWidth /><TextField label="Email address · account ID" value={selectedUser?.email || ''} disabled fullWidth /><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}><TextField label="Phone number" value={phone} onChange={(event) => setPhone(event.target.value)} fullWidth /><TextField label="Date of birth · optional" type="date" value={dateOfBirth} onChange={(event) => setDateOfBirth(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Stack></Stack></Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Account summary</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Role and permissions are managed in the next section.</Typography><Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap><Chip size="small" label={roleId === "admin" ? "Admin" : roles.find((role) => role.id === roleId)?.label || roleId} /><Chip size="small" label={`${projectIds.length} projects`} /><Chip size="small" label={`${pageAccess.length} pages`} /></Stack></Paper>
            </Stack> : <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Role</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Choose this team member’s main responsibility.</Typography><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>{roles.map((role) => <Box key={role.id} component="button" type="button" aria-pressed={roleId === role.id || (role.id === "super-admin" && roleId === "admin")} onClick={() => setRoleId(role.id)} sx={{ textAlign: 'left', border: '1px solid', borderColor: (roleId === role.id || (role.id === "super-admin" && roleId === "admin")) ? '#cbb48e' : '#e8e4dd', borderRadius: 1.5, bgcolor: (roleId === role.id || (role.id === "super-admin" && roleId === "admin")) ? '#fbf7f0' : '#fff', minHeight: 46, px: 1.5, cursor: 'pointer', fontWeight: (roleId === role.id || (role.id === "super-admin" && roleId === "admin")) ? 700 : 500, color: 'text.primary', '&:focus-visible': { outline: '3px solid #c29453', outlineOffset: 2 } }}>{(roleId === role.id || (role.id === "super-admin" && roleId === "admin")) ? '◉' : '○'} &nbsp;{role.label}</Box>)}</Box></Paper>
              {roleId === 'sales-manager' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Reporting team</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select the Sales Team members who report to this manager. Each person has one reporting manager.</Typography><Stack spacing={1}>{salesTeam.filter((member) => member.id !== selectedUser?.id).map((member) => <Box key={member.id} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, border: '1px solid', borderColor: directReportIds.includes(member.id) ? '#cbb48e' : '#e8e4dd', bgcolor: directReportIds.includes(member.id) ? '#fbf7f0' : '#fff', borderRadius: 1.5, p: 1.25, cursor: 'pointer', minHeight: 54 }}><Checkbox checked={directReportIds.includes(member.id)} onChange={(event) => setDirectReportIds((previous) => event.target.checked ? [...previous, member.id] : previous.filter((id) => id !== member.id))} sx={{ p: 0.25 }} /><Avatar sx={{ width: 32, height: 32, bgcolor: '#efe6d8', color: '#805421', fontSize: 12 }}>{String(member.name || '?').charAt(0).toUpperCase()}</Avatar><Box><Typography variant="body2" fontWeight={700}>{member.name}</Typography><Typography variant="caption" color="text.secondary">{member.email}</Typography></Box></Box>)}{salesTeam.filter((member) => member.id !== selectedUser?.id).length === 0 && <Typography variant="body2" color="text.secondary">No Sales Team members yet.</Typography>}</Stack></Paper>}
              {roleId === 'sales-team' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Reports to</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Choose the Sales Manager responsible for this team member.</Typography><FormControl fullWidth><InputLabel id="reports-to-label">Reporting manager</InputLabel><Select labelId="reports-to-label" label="Reporting manager" value={reportsToUserId} onChange={(event) => setReportsToUserId(String(event.target.value))}><MenuItem value="">No manager assigned</MenuItem>{salesManagers.filter((manager) => manager.id !== selectedUser?.id).map((manager) => <MenuItem key={manager.id} value={manager.id}>{manager.name} · {manager.email}</MenuItem>)}</Select></FormControl></Paper>}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Projects</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Choose which projects this person can work on.</Typography><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>{projects.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))).map((project) => <Box key={project.id} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: projectIds.includes(project.id) ? '#cbb48e' : '#e8e4dd', bgcolor: projectIds.includes(project.id) ? '#fbf7f0' : '#fff', borderRadius: 1.5, px: 1.25, minHeight: 46, cursor: 'pointer' }}><Checkbox checked={projectIds.includes(project.id)} onChange={(event) => setProjectIds((previous) => event.target.checked ? [...previous, project.id] : previous.filter((id) => id !== project.id))} size="small" /><Typography variant="body2" fontWeight={600}>{project.name || project.id}</Typography></Box>)}{!projects.length && <Typography variant="body2" color="text.secondary">No projects yet.</Typography>}</Box></Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Pages & actions</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select a page, then choose its actions in the same place.</Typography><Stack spacing={1}>{allPages.map((page) => { const selected = pageAccess.includes(page.key); const actions = pageActions[page.key] || []; return <Box key={page.key} sx={{ border: '1px solid', borderColor: selected ? '#cbb48e' : '#e8e4dd', borderRadius: 1.5, bgcolor: selected ? '#fffdfa' : '#fff', overflow: 'hidden' }}><Box component="label" sx={{ display: 'flex', alignItems: 'center', px: 1.25, minHeight: 58, cursor: 'pointer' }}><Checkbox checked={selected} onChange={(event) => togglePage(page.key, event.target.checked)} size="small" /><Box sx={{ flex: 1 }}><Typography variant="body2" fontWeight={700}>{page.label}</Typography><Typography variant="caption" color="text.secondary">{actions.length ? `${actions.length} available actions` : 'View access'}</Typography></Box>{selected && <Chip label={actions.length ? `${actions.filter((action) => isDomainActionSelected(page.key, action.key, actionAccess)).length} selected` : 'View'} size="small" sx={{ bgcolor: '#f6f0e7', color: '#805421' }} />}</Box>{selected && actions.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5, px: 2, pb: 1.5, pt: 0.75, borderTop: '1px solid #e8e4dd' }}>{actions.map((action) => { const value = toDomainAction(page.key, action.key); return <Box component="label" key={`${page.key}-${action.key}`} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minHeight: 38 }}><Checkbox size="small" checked={actionAccess.includes(value)} onChange={(event) => toggleAction(value, event.target.checked)} /><Typography variant="body2">{action.label === 'Assign Change' ? 'Change assignment' : action.label}</Typography></Box>; })}</Box>}</Box>; })}</Stack></Paper>
            </Stack>}
            {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid #e8e4dd', bgcolor: '#fff' }}><Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Changes are saved when you select Save.</Typography><Stack direction="row" spacing={1} sx={{ ml: 'auto' }}><Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button></Stack></Stack>
        </Box>
      </Drawer>

      <Dialog open={deleteDialogOpen} onClose={() => !deletingUser && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer leads before deleting user</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {pendingDelete?.user.name} still has {pendingDelete?.leadCount || 0} assigned
              {' '}lead{pendingDelete?.leadCount === 1 ? '' : 's'}.
            </Typography>
            {pendingDelete?.leadNames?.length ? (
              <Alert severity="info" variant="outlined">
                Example leads: {pendingDelete.leadNames.join(', ')}
              </Alert>
            ) : null}
            <FormControl fullWidth size="small">
              <InputLabel id="delete-transfer-target-label">New Assignee</InputLabel>
              <Select
                labelId="delete-transfer-target-label"
                value={deleteTarget}
                label="New Assignee"
                onChange={(e) => setDeleteTarget(String(e.target.value || ''))}
              >
                <MenuItem value="__unassigned__">Unassigned</MenuItem>
                {users
                  .filter((user) => user.email !== pendingDelete?.user.email)
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.email}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {deleteDialogError ? <Alert severity="error">{deleteDialogError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setPendingDelete(null);
              setDeleteTarget('');
              setDeleteDialogError(null);
            }}
            disabled={deletingUser}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteWithTransfer} variant="contained" color="error" disabled={deletingUser}>
            {deletingUser ? 'Transferring…' : 'Transfer & Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
